import {throwError as observableThrowError,  Observable ,  BehaviorSubject, Subject, of } from 'rxjs';
import { tap, map, catchError } from 'rxjs/operators';
import { HttpClient, HttpHeaders, HttpResponse, HttpHeaderResponse } from '@angular/common/http';
import { Injectable, ErrorHandler } from '@angular/core';
import { CachingService } from '../http-service/caching.service';
import { AUTH_ROUTE, REFRESH_ROUTE, baseHOST, HOST } from '../../routes';
import { AuthResponse } from './auth-response';
import { StorageService } from '../storage-service/storage.service';
import { Router } from '@angular/router';
// import { ToastManagerService } from '../toastmanager/toast-manager.service';

/*
* AuthService
*/
@Injectable()
export class AuthService {
  private accessToken: string = null;
  private refreshToken: string = null;
  public resetPasswordUrl = baseHOST + '/accounts/reset';
  private authStatus: BehaviorSubject<boolean>;
  tokenRefreshSubject: Subject<boolean> = new Subject();
  refreshInProgress = false;

  readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json'
    }),
    observe: 'response' as 'body'
  };

  constructor(private storage: StorageService, private cache: CachingService, private http: HttpClient, private errorHandler: ErrorHandler, private router: Router) {
    this.authStatus = new BehaviorSubject<boolean>(false);
    this.init();
  }

  /**
   * Initialize AuthService
   */
  private init() {
    const token = this.storage.getItem('token');
    const refreshToken = this.storage.getItem('refresh_token');

    if (token != null && refreshToken != null) {
      // Local authentication token available
      this.accessToken = token;
      this.refreshToken = refreshToken;
      this.authStatus.next(true);
    }
    this.tokenRefreshSubject.subscribe(val => {
      this.refreshInProgress = false;
      if (val === false) {
        this.logout();
        this.authStatus.next(false);
        this.router.navigate(['/login']);
      }
    });
  }

  /**
   * Log user in
   * @param username [description]
   * @param password [description]
   * @return true, if login is successfull
   */
  public login(username: string, password: string): Observable<boolean> {
    if (username === null || password === null || username.trim().length === 0) {
      this.authStatus.next(false);
      return observableThrowError('Benutzername und Passwort angeben!');
    } else {
      return Observable.create(observer => {
        this.authenticate(username, password)
          .then(body => {
            this.saveToken(body);

            this.authStatus.next(true);
            observer.next(true);
            observer.complete();
          })
          .catch(error => {
            this.authStatus.next(false);
            observer.error(error.statusText);
          });
      });
    }
  }

  private saveToken(body: AuthResponse) {
    this.accessToken = body.token;
    this.refreshToken = body.refresh_token;
    this.storage.setItem('token', body.token);
    this.storage.setItem('refresh_token', body.refresh_token);
  }

  /**
   * Authenticate the user
   * @param username username
   * @param password password
   */
  private authenticate( username: string, password: string): Promise<AuthResponse> {
    const body = {
      username: username,
      password: password
    };

    return new Promise((resolve, reject) => {
      this.http.post<any>(AUTH_ROUTE, body, this.httpOptions).subscribe(
        (data: HttpResponse<AuthResponse>) => {
          if (data.ok) {
            resolve(data.body);
          } else {
            reject();
          }
        },
        error => {
          reject(error);
        }
      );
    });
  }

  /**
   * Refresh access token reworked
   * @return obervable bool; true if request successful
   */
  public refreshAccessToken(): Observable<HttpResponse<AuthResponse>> {
    this.refreshInProgress = true;
    const body = {
      refresh_token: this.refreshToken
    };

    // return new Observable((observer) => {
    //   this.http.post(AUTH_ROUTE, body, this.httpOptions).
    // })
    return this.http
      .post<HttpResponse<AuthResponse>>(REFRESH_ROUTE, body, this.httpOptions)
      .pipe( tap(
        res => {
          if (res.ok) {
            this.authStatus.next(true);
            this.tokenRefreshSubject.next(true);
            this.saveToken(res.body);
            return true;
          } else {
            this.tokenRefreshSubject.next(false);
            return false;
          }
        }, (err) => {
          console.log('error!', err);
          this.tokenRefreshSubject.next(false);
          return false;
        }
      ));
  }

  public newLogin(username: string, password: string): Observable<boolean> {
    const body = {
      client_id: '2_randomid',
      client_secret: 'secret',
      grant_type: 'password',
      username,
      password
    };

    const toastTitle = 'Bitte warten...';
    const toastMessage = 'Login';

    // this.toastManager.createToast('Bitte warten...', toastMessage, true, 'wait', true);
    // this.toastManager.showError('test');

    const observable = this.http
      .post<HttpResponse<AuthResponse>>(AUTH_ROUTE, body, this.httpOptions)
      .pipe( map(res => {
        if (res.ok) {
          this.saveToken(res.body);
          this.authStatus.next(true);
          return true;
        } else {
          // this.toastManager.showError(toastMessage);
          return false;
        }
      }),
    //   catchError(err => {
    //     this.errorHandler.handleError(err);
    //     // this.toastManager.showError(toastMessage);
    //     return of(false);
    // })
    );

    return observable;
  }

  /**
   * Logs the current user out
   */
  public logout(): void {
    // set auth status subject
    // this.logoutRequest().pipe(catchError( err => {
    //   return of(null);
    // })).subscribe( () => {});
    this.authStatus.next(false);

    // Remove tokens from storage
    this.storage.deleteItem('token');
    this.storage.deleteItem('refresh_token');

    this.accessToken = null;
    this.refreshToken = null;

    // clear data in cache
    // this.cache.clearCache();
  }

  private logoutRequest(): Observable<HttpHeaderResponse> {
    const url = HOST + '/logout';

    return this.http.post<HttpHeaderResponse>(url, this.httpOptions);
  }

  /**
   * Current auth status
   * @return true, if user is authenticated. null, if the status is unknown or not yet initialized
   */
  public isLoggedIn(): boolean {
    return this.authStatus.value;
  }

  /**
   * Get the current access token
   * @return access token
   */
  get authorizationHeader(): string {
    if (this.accessToken != null) {
      return 'Bearer ' + this.accessToken;
      // return 'Bearer MDAxNjllYTBmMzJlMDQ5ZThkODc1OWRjY2IxZDM5OGUxZTVlYmI2OTQ0N2NkODBiNzlhNmEzMjYzMzczZTJjNQ';
    }
    return null;
  }

  requestPasswordMail(mail: string): Observable<HttpHeaderResponse> {
    const url = baseHOST + '/accounts/resetting/request';
    const body = {
      email: mail
    };
    return this.http.post<HttpHeaderResponse>(url, body, this.httpOptions);
  }

  resetPassword(pw: string, token: string): Observable<HttpHeaderResponse> {
    const url = baseHOST +  '/accounts/resetting/' + token;
    const body = {
      password: pw,
    };
    return this.http.post<HttpHeaderResponse>(url, body, this.httpOptions);
  }
}